import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductImagesSection } from "./ProductImagesSection";
import { SellerInfoSection } from "./SellerInfoSection";
import { ContactFormDialog } from "../ContactFormDialog";
import { MessageDialog } from "../MessageDialog";
import { PurchaseRequestDialog } from "./PurchaseRequestDialog";
import { type Product, type Quote } from "./types";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ServiceMap } from "../ServiceMap";
import { SimilarItems } from "../components/SimilarItems";

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);

  // Record product view
  useEffect(() => {
    const recordProductView = async () => {
      if (id) {
        try {
          // Use RPC to record product view with type assertion to handle TypeScript error
          const { error } = await supabase.rpc(
            'record_product_view' as any, 
            {
              product_id_param: id
            }
          );
          
          if (error) {
            console.error("Failed to record view:", error);
          } else {
            console.log("Product view recorded");
          }
        } catch (error) {
          console.error("Error in recordProductView:", error);
        }
      }
    };

    recordProductView();
  }, [id]);

  const { data: product, isLoading, error } = useQuery<Product, Error>({
    queryKey: ["product", id],
    queryFn: async () => {
      if (!id) {
        throw new Error("Product ID is missing");
      }
      
      console.log("Fetching product details for ID:", id);
      
      const { data, error } = await supabase
        .from("professional_products")
        .select(`
          *,
          profiles:professional_id (
            full_name,
            profile_photo,
            rating,
            response_rate,
            response_time,
            total_reviews,
            practice_address
          )
        `)
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching product:", error);
        throw error;
      }
      
      console.log("Product data received:", data);
      
      // Parse quotes from JSON
      let parsedQuotes: Quote[] = [];
      if (data.quotes) {
        try {
          if (typeof data.quotes === 'string') {
            parsedQuotes = JSON.parse(data.quotes);
          } else if (Array.isArray(data.quotes)) {
            // Cast each item as any before accessing properties
            parsedQuotes = (data.quotes as any[]).map(q => ({
              id: q?.id || "",
              name: q?.name || "",
              price: typeof q?.price === 'number' ? q?.price : parseFloat(q?.price || '0') || 0
            }));
          } else if (typeof data.quotes === 'object') {
            // Handle Supabase JSON object format
            const quotesJson = data.quotes as any;
            if (Array.isArray(quotesJson)) {
              parsedQuotes = quotesJson.map(q => ({
                id: q?.id || "",
                name: q?.name || "",
                price: typeof q?.price === 'number' ? q?.price : parseFloat(q?.price || '0') || 0
              }));
            }
          }
        } catch (e) {
          console.error("Error parsing quotes:", e);
        }
      }
      
      return {
        ...data,
        quotes: parsedQuotes
      } as Product;
    },
    meta: {
      onSettled: (data: Product | undefined, err: Error | null) => {
        if (err) {
          console.error("Query error:", err);
          toast.error("Erreur lors du chargement des détails du produit");
          // Rediriger vers la page des services si le produit n'est pas trouvé
          navigate("/services");
        }
      }
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Skeleton className="h-[400px] w-full rounded-xl" />
              <div className="grid grid-cols-4 gap-2">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-5 w-1/4" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-[180px] w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    console.error("Error rendering product details:", error);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-xl font-bold text-red-500 mb-3">
            Impossible de charger ce produit
          </h2>
          <p className="mb-4 text-muted-foreground text-sm">
            Nous n'avons pas pu trouver les détails de ce produit. Il est possible qu'il ait été supprimé
            ou qu'une erreur se soit produite.
          </p>
          <button
            onClick={() => navigate("/services")}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm"
          >
            Retour aux services
          </button>
        </div>
      </div>
    );
  }
  
  // Prepare location data for map - adapting the data format for ServiceMap
  const locationData = product && product.address ? [{
    id: product.id,
    service_name: product.title, // Use product title as service_name
    description: product.description,
    price: product.price,
    duration: 0, // Required by Service type
    photo_url: product.photo_url,
    photos: product.photos,
    phone: product.phone,
    address: product.address,
    email: null,
    country: null,
    created_at: product.created_at,
    updated_at: product.updated_at,
    professional_id: product.professional_id,
    latitude: null,
    longitude: null,
    profiles: product.profiles
  }] : [];

  // Get product category - could be based on title or other product characteristics
  const productCategory = product.condition || "general";

  const handlePurchaseRequest = () => {
    setIsPurchaseDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <ProductImagesSection product={product} />
            
            {/* Map section - more compact */}
            {(product.address || product.profiles?.practice_address) && (
              <div className="mt-4">
                <h3 className="text-base font-medium mb-2">Localisation</h3>
                <div className="h-[200px] rounded-xl overflow-hidden">
                  <ServiceMap services={locationData} />
                </div>
              </div>
            )}
          </div>
          <div>
            <SellerInfoSection
              seller={product.profiles ? {
                ...product.profiles,
                id: product.professional_id
              } : {
                id: product.professional_id,
                full_name: "Vendeur",
              }}
              product={product}
              onPurchaseRequest={handlePurchaseRequest}
            />
          </div>
        </div>
        
        {/* Similar Products Section */}
        <SimilarItems 
          type="product" 
          currentItemId={product.id} 
          category={productCategory}
        />
      </main>

      <ContactFormDialog
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        productTitle={product.title}
        productId={product.id}
        professionalId={product.professional_id}
      />

      <MessageDialog
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
        professionalId={product.professional_id}
        professionalName={product.profiles?.full_name || ""}
        context="product"
        itemTitle={product.title}
      />

      <PurchaseRequestDialog
        open={isPurchaseDialogOpen}
        onOpenChange={setIsPurchaseDialogOpen}
        productId={product.id}
        professionalId={product.professional_id}
        productTitle={product.title}
        productPrice={product.price}
      />
    </div>
  );
};
